import type {
  AgentInstanceOption,
  PullRequestCreationOptions,
} from "../Findings/FindingDetailsPane/AgentRun/types";

export const ANALYST_RUN_STATUS = {
  PENDING: "pending",
  RUNNING: "running",
  SUCCESS: "success",
  FAILURE: "failure",
} as const;

export type AnalystRunStatus =
  (typeof ANALYST_RUN_STATUS)[keyof typeof ANALYST_RUN_STATUS];

export const ANALYST_INSTANCE_STATUS = {
  ACTIVE: "active",
  ERROR: "error",
} as const;

export type AnalystInstanceStatus =
  (typeof ANALYST_INSTANCE_STATUS)[keyof typeof ANALYST_INSTANCE_STATUS];

export interface AnalystInstance {
  agent_instance_id: string;
  display_name: string;
  agent_type: string;
  agent_type_label: string;
}

export interface AnalystRunFields {
  run_id: string;
  finding_id: string;
  completed_at: string;
  original_priority: string;
  suggested_priority: string;
  reasoning_summary: string;
  agent_instance_id: string;
  analyst_name: string;
  agent_type: string;
  agent_type_label: string;
  run_by_automation_id: string | null;
  finding_title: string | null;
  finding_resource_name: string | null;
  finding_is_group_member: boolean | null;
}

export interface AnalystRunResult<Fields = AnalystRunFields> {
  entity_id: string | null;
  status: AnalystRunStatus;
  fields?: Fields;
}

export interface AnalystRunsPagination {
  limit: number;
  offset: number;
  total_count: number | null;
}

export interface AnalystRunsResponse<Fields = AnalystRunFields> {
  id: string;
  type: string;
  status: string;
  timestamp: string;
  pagination: AnalystRunsPagination;
  results: AnalystRunResult<Fields>[];
}

export const INTEGRATION_CATEGORY = {
  TICKET: "TICKET",
  DATASOURCE: "DATASOURCE",
} as const;
export type IntegrationCategory =
  (typeof INTEGRATION_CATEGORY)[keyof typeof INTEGRATION_CATEGORY];

export interface AnalystInstanceIntegration {
  id: number;
  integration_type: string;
  integration_category?: IntegrationCategory;
  type: string;
  display_name: string;
}

export interface AnalystInstanceStats {
  analyzed_findings: number;
  last_run_at: string | null;
}

export interface AnalystInstanceBase {
  agent_instance_id: string;
  display_name: string;
  agent_type: string;
  agent_type_label: string;
  integrations: AnalystInstanceIntegration[];
  options?: AgentInstanceOption[];
  is_on_prem?: boolean;
}

export interface AnalystInstanceDetails {
  agent_instance_id: string;
  status: AnalystInstanceStatus;
  status_comment: string | null;
  stats: AnalystInstanceStats;
}

export const AUTOMATION_STATUS = {
  LOADING: "loading",
  ERROR: "error",
  AUTOMATED: "automated",
  NOT_AUTOMATED: "not-automated",
} as const;

export type AutomationStatus =
  (typeof AUTOMATION_STATUS)[keyof typeof AUTOMATION_STATUS];

export const ANALYST_AVAILABILITY = {
  CONFIGURED: "CONFIGURED",
  CONTACT_SUPPORT: "CONTACT_SUPPORT",
  COMING_SOON: "COMING_SOON",
} as const;

export type AnalystAvailability =
  (typeof ANALYST_AVAILABILITY)[keyof typeof ANALYST_AVAILABILITY];

export interface AgentDefinition {
  agent_type: string;
  display_name: string;
  description: string;
  generally_available: boolean;
}

export interface AgentDefinitionsResponse {
  agent_definitions: AgentDefinition[];
}

export interface AnalystType extends AgentDefinition {
  availability: AnalystAvailability;
}

export interface AnalystCardIntegration {
  type: string;
  displayName: string;
}

export interface AnalystCardStats {
  analyzedFindings: number;
  lastRunAt: string | null;
}

export interface AnalystInstanceCard {
  agentInstanceId: string;
  displayName: string;
  agentType: string;
  agentTypeLabel: string;
  integrations: AnalystCardIntegration[];
  isOnPrem: boolean;
  status?: AnalystInstanceStatus;
  statusComment?: string | null;
  stats?: AnalystCardStats;
  automationStatus?: AutomationStatus;
}

export type AnalystPlaceholderAvailability = Exclude<
  AnalystAvailability,
  typeof ANALYST_AVAILABILITY.CONFIGURED
>;

export interface AnalystPlaceholder {
  agentType: string;
  displayName: string;
  availability: AnalystPlaceholderAvailability;
}

export interface AnalystInstancesResponse {
  agent_instances: AnalystInstanceBase[];
}

export interface AnalystInstancesDetailsResponse {
  instances: AnalystInstanceDetails[];
}

export interface AnalystInstanceMetadata extends AnalystInstanceBase {
  description: string;
  created_by: string;
  created_at: string;
}

// Read shape: the service returns a saved scope in `scopesid` and a scope group
// in `scope_group`, one or the other.
export interface AnalystFiltersConfig {
  filtersid: string;
  scopesid: string | null;
  scope_group: string | null;
  filters_fields_to_override?: string[];
}

// Write shape: scope always goes out as `scopesid`, group or not.
export interface AnalystFiltersConfigInput {
  filtersid: string;
  scopesid: string;
}

// Reading `scopesid` alone drops every automation scoped to a group.
export const getAnalystScopeId = (
  filtersConfig: AnalystFiltersConfig
): string | null => filtersConfig.scopesid ?? filtersConfig.scope_group;

export interface AnalystAutomationError {
  type: string;
  message: string;
}

export interface AnalystAutomationConfig {
  pr_creation?: PullRequestCreationOptions | null;
}

export interface AnalystAutomation {
  automation_id: string;
  entity_id: string;
  name: string;
  enabled: boolean;
  description: string;
  error: AnalystAutomationError | null;
  filters_config: AnalystFiltersConfig;
  config?: AnalystAutomationConfig;
}

export interface AnalystAutomationsResponse {
  automations: AnalystAutomation[];
}

export interface CreateAnalystAutomationRequest
  extends Pick<AnalystAutomation, "name" | "description"> {
  filters_config: AnalystFiltersConfigInput;
  config?: AnalystAutomationConfig;
}

export interface CreateAnalystAutomationResponse {
  automation_id: string;
}

export type UpdateAnalystAutomationRequest = Partial<
  CreateAnalystAutomationRequest & Pick<AnalystAutomation, "enabled">
>;

export interface AnalystCreditBatch {
  id: number;
  credits_purchased: number;
  credits_used: number;
  start_date: string;
  expiry_date: string;
  batch_type: string | null;
}

export interface CreditPack {
  id: number;
  credits_purchased: number;
  credits_used: number;
  start_date: string;
  expiry_date: string;
  batch_type: string | null;
  notes: string | null;
}

export interface CreditPacksResponse {
  credit_packs: CreditPack[];
}

export interface CreateCreditPackRequest {
  credits_purchased: number;
  purchase_date?: string;
  expiry_date?: string;
  batch_type?: string | null;
  notes?: string | null;
}

export const INTEGRATION_TYPE = {
  JIRA: "JIRA",
  JIRA_ON_PREM: "JIRA_ON_PREM",
  GHB: "GHB",
  GITHUB_INVENTORY: "GITHUB_INVENTORY",
  GITHUB_CODE_SCANNING: "GITHUB_CODE_SCANNING",
  GITHUB_ENTERPRISE: "GITHUB_ENTERPRISE",
  GITLAB_SCA: "GITLAB_SCA",
  BITBUCKET: "BITBUCKET",
  WIZ: "WIZ",
  CRWD: "CRWD",
  AWS: "AWS",
  AZR: "AZR",
} as const;
export type IntegrationTypeValue =
  (typeof INTEGRATION_TYPE)[keyof typeof INTEGRATION_TYPE];

export interface AvailableIntegration {
  id: number;
  type: string;
  integration_type: string;
  display_name: string;
  integration_category: IntegrationCategory | null;
}

export interface AvailableIntegrationsResponse {
  integrations: AvailableIntegration[];
}

export interface IntegrationInstance {
  id: number;
  integration_type: string;
  display_name: string;
}
export interface AnalystInstanceConfig {
  integration_instances: IntegrationInstance[];
}
export interface AddAnalystInstanceRequest {
  agent_type: string;
  name: string;
  config: AnalystInstanceConfig;
}
export interface AddAnalystInstanceResponse {
  agent_instance_id: number;
  display_name: string;
  agent_type: string;
}
export interface UpdateAnalystInstanceRequest {
  name: string;
  config: AnalystInstanceConfig;
}
